import { v4 as uuidv4 } from 'uuid';
import { ForbiddenError } from './errors';

function ApiManager(sessionToken, GSendpoint, errorCallback, setToken, maxRate = 10, abort) {

    let subscriptionBucket;
    let refreshing = false;
    let server = "";
    let apiRequestTime = Date.now();
    let apiRequestCount = 0;
    let maxApiRateHit = 0;

    const basicHeaders = {
        "Accept": "application/ld+json",
        "Accept-encoding": "gzip, deflate"
    };

    this.getServer = function () {
        return server;
    };

    this.setSubscriptionBucket = function (bucket) {
        subscriptionBucket = bucket;
    };

    // If bucket is given we route the request to that server
    this.get = async function (endpoint, format = "application/json", codesToHandle) {
        return getExternalResponse(endpoint, {
            method: 'GET',
            headers: new Headers({ "Accept": format }),
            mode: 'cors',
            cache: 'default'
        }, codesToHandle);
    };

    // If bucket is given we route the request to that server
    this.getData = async function (endpoint, format = "application/ld+json", bucket, codesToHandle, alwaysThrow = false) {
        return getResponse(endpoint, GETrequestBuilder({
            "Accept": format,
            ...(bucket) && { "Graphologi-Target": bucket }
        }), codesToHandle, alwaysThrow);
    };

    this.deleteData = async function (endpoint) {
        return getResponse(endpoint, DELETErequestBuilder());
    };

    this.uploadData = async function (formData, projectIRI, graphIRI, subscription) {
        return getResponse(`/data?project=${projectIRI}&subscription=${subscription}`, POSTrequestBuilder({}, formData));
    };

    this.sparql = async function (endpoint, formData, codesToHandle) {
        return getResponse(endpoint, POSTrequestBuilder({
            "Accept": "application/sparql-results+json"
        }, formData), codesToHandle);
    };

    this.uploadProject = async function (formData, subscription, projectType) {
        return getResponse(`/project?type=${projectType}&subscription=${subscription}`, POSTrequestBuilder({}, formData));
    };

    this.uploadExampleProject = async function (id, payload, subscription) {
        return getResponse(`/project/example/${id}?subscription=${subscription}`, POSTrequestBuilder({
            "Cache-Control": "no-cache",
            "Content-Type": "application/json"
        }, JSON.stringify(payload)));
    };

    this.uploadFeedback = async function (formData) {
        return getResponse(`/feedback`, POSTrequestBuilder({}, formData));
    };

    this.uploadStatus = async function (uuid, subscription, format = "application/ld+json") {
        return getResponse(`/data/${uuid}?subscription=${subscription}`, GETrequestBuilder({
            "Accept": format
        }));
    };

    this.sendData = async function (endpoint, payload, suppliedOptions, codesToHandle) {
        let options = {
            format: "application/json",
            accept: "application/json"
        };
        const finalOptions = Object.assign(options, suppliedOptions);
        return getResponse(endpoint, POSTrequestBuilder({
            "Cache-Control": "no-cache",
            "Content-Type": finalOptions.format,
            "Accept": finalOptions.accept
        }, JSON.stringify(payload), suppliedOptions), codesToHandle);
    };

    this.updateData = async function (endpoint, payload, suppliedOptions) {
        let options = {
            format: "application/json",
            accept: "application/json"
        };
        const finalOptions = Object.assign(options, suppliedOptions);
        return getResponse(endpoint, PATCHrequestBuilder({
            "Cache-Control": "no-cache",
            "Content-Type": finalOptions.format,
            "Accept": finalOptions.accept
        }, JSON.stringify(payload), suppliedOptions), [403]);
    };

    function headersBuilder(addHeaders) {
        let customHeaders = Object.assign({}, basicHeaders);
        customHeaders.Authorization = "Bearer " + sessionToken;
        if (subscriptionBucket) {
            customHeaders["Graphologi-Target"] = subscriptionBucket;
        }
        for (let property in addHeaders) {
            if (addHeaders.hasOwnProperty(property)) {
                customHeaders[property] = addHeaders[property];
            }
        }
        return new Headers(customHeaders);
    }

    function GETrequestBuilder(addHeaders) {
        return {
            method: 'GET',
            headers: headersBuilder(addHeaders),
            mode: 'cors',
            cache: 'default'
        };
    }

    function DELETErequestBuilder(addHeaders) {
        return {
            method: 'DELETE',
            headers: headersBuilder(addHeaders),
            mode: 'cors',
            cache: 'no-cache'
        };
    }

    function POSTrequestBuilder(addHeaders, bodyText, suppliedOptions = {}) {
        return {
            method: 'POST',
            headers: headersBuilder(addHeaders),
            mode: 'cors',
            cache: 'no-cache',
            body: bodyText,
            ...(suppliedOptions.cookies) && { "credentials": "include" }
        };
    }

    function PATCHrequestBuilder(addHeaders, bodyText, suppliedOptions = {}) {
        return {
            method: 'PATCH',
            headers: headersBuilder(addHeaders),
            mode: 'cors',
            cache: 'no-cache',
            body: bodyText,
            ...(suppliedOptions.cookies) && { "credentials": "include" }
        };
    }

    async function getExternalResponse(endpoint, reqHeaders, codesToHandle = []) {
        let res;
        try {
            res = await fetch(endpoint, reqHeaders);
            if (res.ok) return res;
            // We give JSON error responses but AWS might give something else so make sure we check
            const contentType = res.headers.get("content-type") || "";
            const responseFormat = contentType.split(";").map(value => value.trim());
            const errorData = responseFormat.includes("application/json") ? await res.json() : { message: "An unknown problem occurred." };
            if (res.status === 401 || res.status === 403) {
                throw new ForbiddenError(errorData.message);
            }
            else if (codesToHandle.includes(res.status)) {
                return res;
            }
            else throw new Error(errorData.message);
        }
        catch (e) {
            if (res && res.status && codesToHandle.includes(res.status)) {
                return res;
            }
            if (e.message === "Failed to fetch") {
                throw new Error("There seem to be connection issues.\nThe service you are trying to access may not be available.");
            }
            throw e;
        }
    }

    function sleepFor(t) {
        return new Promise(resolve => setTimeout(resolve, t));
    }

    async function doRefresh() {
        const endpoint = process.env.REACT_APP_API_URL;
        let headers = {
            "Accept": "application/ld+json",
            "Accept-encoding": "gzip, deflate"
        };
        // We include the refresh token and user cookies if set
        const res = await fetch(endpoint + "/authenticate/refresh", {
            method: 'GET',
            headers: new Headers(headers),
            credentials: 'include',
            mode: 'cors',
            cache: 'default'
        });
        if (res.ok) {
            const userData = await res.json();
            return userData;
        }
        else {
            return false;
        }
    }

    // In some situations we want to handle certain codes rather than throw errors
    // At some point it might be useful to add the UUID as header in the request to track
    async function getResponse(endpoint, reqHeaders, codesToHandle = [], alwaysThrow = false, uuid = uuidv4()) {
        let res;
        try {
            const currentTime = Date.now();
            // Reset count after 1 second
            if (currentTime > apiRequestTime + 1000) {
                apiRequestTime = currentTime;
                apiRequestCount = 0;
            }
            // If we are approaching rate limit pause
            if (refreshing) {
                console.log("Pausing API request whilst refreshing token: ", uuid, endpoint);// eslint-disable-line
                await sleepFor(1000);
                // We update the session token on the assumption it has been updated
                const headers = reqHeaders.headers;
                headers.set("Authorization", "Bearer " + sessionToken);
                return getResponse(endpoint, reqHeaders, codesToHandle, alwaysThrow, uuid);
            }
            // If we are approaching rate limit pause
            if (apiRequestCount > maxRate - 3) {
                console.log("Pausing API request. Rate is: ", apiRequestCount, uuid);// eslint-disable-line
                await sleepFor(1000);
                // Try again after a period of time
                return getResponse(endpoint, reqHeaders, codesToHandle, alwaysThrow, uuid);
            }
            apiRequestCount++;
            if (apiRequestCount > maxApiRateHit) {
                maxApiRateHit = apiRequestCount;
            }
            // We use the same controller on all fetch requests so that on logout everything gets killed
            reqHeaders.signal = abort.signal;
            res = await fetch(GSendpoint + endpoint, reqHeaders);
            if (res.ok || res.status === 415) {
                const gsServer = res.headers.get("gs-server");
                if (gsServer) {
                    if (server !== gsServer) {
                        console.log("Switch of server to use: " + gsServer);// eslint-disable-line
                    }
                    server = gsServer;
                }
                return res;
            }
            else {
                // We give JSON error responses but AWS might give something else so make sure we check
                const contentType = res.headers.get("content-type") || "";
                const responseFormat = contentType.split(";").map(value => value.trim());
                const errorData = responseFormat.includes("application/json") ?
                    await res.json()
                    :
                    { message: "An unknown problem occurred." };
                if (res.status === 401 || res.status === 403) {
                    // If a token has expired we refresh, but there on subscription creation a token could get revoked
                    // due to server creation so we also refresh in that situation too
                    if (errorData.message === "jwt expired" || errorData.message === "The token has been revoked.") {
                        // Try getting a new access token from refresh token
                        try {
                            if (!refreshing) {
                                refreshing = true;
                                const isRefreshed = await doRefresh();
                                if (isRefreshed) {
                                    sessionToken = isRefreshed.token;
                                    // Update GS
                                    setToken(sessionToken);
                                    refreshing = false;
                                    // Update the request token
                                    const headers = reqHeaders.headers;
                                    headers.set("Authorization", "Bearer " + sessionToken);
                                    // Now try original call again
                                    return getResponse(endpoint, reqHeaders, codesToHandle, alwaysThrow, uuid);
                                }
                                else {
                                    refreshing = false;
                                    if (alwaysThrow) {
                                        throw new ForbiddenError(errorData.message);
                                    }
                                    errorCallback("Your session has expired. You need to log back in to continue.", true);
                                }
                            }
                            else {
                                // Retry query again, which will be paused until refreshing done (see above)
                                return getResponse(endpoint, reqHeaders, codesToHandle, alwaysThrow, uuid);
                            }
                        }
                        catch (e) {
                            if (alwaysThrow) {
                                throw e;
                            }
                            refreshing = false;
                            errorCallback("Your session has expired and we cannot refresh the token. You need to log back in to continue.", true);
                        }
                    }
                    else {
                        throw new ForbiddenError(errorData.message);
                    }
                }
                else if (codesToHandle.includes(res.status)) {
                    return res;
                }
                // This is caused by a failover switch where the resource controller doesn't recognise the socket ID
                else if (res.status === 400 && errorData.message.startsWith("User not recognised")) {
                    errorCallback("There appears to be a connection issue caused by network rerouting. Please try logging out and back in.");
                    return;
                }
                else if (res.status === 500 && errorData.message === "Subscription has failed.") {
                    throw new Error("Unfortunately there has been an issue with processing an operation on this subscription.\nThis subscription will be temporarily suspended whilst support investigates.\nThey will automatically be notified.");
                }
                else if (res.status === 500 && errorData.message.includes("The payload is too large.")) {
                    throw new Error(errorData.message);
                }
                else if (res.status === 500 || res.status === 502 || res.status === 503) {
                    const explanation = errorData && errorData.explanation ? "\n" + errorData.explanation : "";
                    errorCallback("A system error occurred. Please try logging out and back in. If the problem persists please contact support." + explanation);
                    return;
                }
                else if (res.status === 504) {
                    // ELB timeout normally
                    errorCallback("A network timeout occurred. An operation may be taking too long to run. Please try logging out and back in. If the problem persists please contact support.");
                    return;
                }
                else {
                    throw new Error(errorData.message);
                }
            }
        }
        catch (e) {
            if (e.message === "Failed to fetch") {
                errorCallback("We are unable to connect to Graphologi at this time. Please try again later.");
            }
            else if (res && res.status && codesToHandle.includes(res.status)) {
                return res;
            }
            throw e;
        }
    }
}

export default ApiManager;